import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { homeService, memberService } from '../clients/ApiClients';
import {
  HoaReturn,
  HomeCreate,
  HomeReturn,
  MemberReturn,
} from '../clients/rust-api-client';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../LoadingSpinner';
import { Heading } from '../common/Catalyst/heading';
import { TrashIcon } from '@heroicons/react/16/solid';
import { ListFilterBar } from '../shell/ListFilterBar';
import { CreateButton } from '../shell/CreateButton';
import { SelectionBar } from '../violation/linear/RowCheckbox';
import { GroupedList } from '../shell/GroupedList';
import {
  HOME_STATUSES,
  HomeFilters,
  PROPERTY_TYPES,
  applyHomeFilters,
  groupHomes,
  homeAddressLine,
  homeLocality,
  normalizeHomeStatus,
  propertyTypeLabel,
} from './homeMeta';
import ShowAlert from '../ShowAlert';
import { hasAdminAccess } from '../constants/UserTypes';
import { getUserTypeFromToken } from '../utils/TokenUtils';
import AddHomeDialog from './AddHomeDialog';
import AddHomeToMemberDialog from './AddHomeToMemberDialog';
import { useSyncedHomes } from '../sync/hooks';
import { apiErrorMessage } from '../utils/apiErrors';

interface HomeListComponentProps {
  member?: MemberReturn | null;
  hoaId?: string | null;
}

interface AlertState {
  type: 'success' | 'error';
  title: string;
  message: string;
  show: boolean;
}

const HomeListComponent: React.FC<HomeListComponentProps> = ({
  member,
  hoaId,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [fetchedHomes, setHomes] = useState<HomeReturn[]>([]);
  const [hoas] = useState<HoaReturn[]>([]);
  // Linear-style filters (#603 follow-on 3).
  const [filters, setFilters] = useState<HomeFilters>({ statuses: [], types: [], search: '' });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isAddToMemberDialogOpen, setIsAddToMemberDialogOpen] = useState(false);
  const [selectedHomes, setSelectedHomes] = useState<Set<string>>(new Set());
  const [alert, setAlert] = useState<AlertState>({
    type: 'success',
    title: '',
    message: '',
    show: false,
  });

  const userType = useMemo(() => getUserTypeFromToken(), []);

  const hideAlert = () => {
    setAlert((prev) => ({ ...prev, show: false }));
  };

  const showAlert = (
    type: 'success' | 'error',
    title: string,
    message: string
  ) => {
    setAlert({
      type,
      title,
      message,
      show: true,
    });
  };

  const memberId = member?.id;

  // From the sync engine's local copy when it has one (#561): no request,
  // and a change made anywhere — another tab, another manager — lands
  // here without a refetch. Fetching below is the fallback.
  const synced = useSyncedHomes(hoaId ? { hoaId } : { memberId });
  const fromStore = synced !== null && !!(hoaId || memberId);
  const homes = fromStore && synced ? synced : fetchedHomes;

  const fetchHomes = useCallback(async () => {
    if (fromStore) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      if (hoaId) {
        const response = await homeService.getHomesByHoa({ hoaId });
        setHomes(response.data);
      } else if (memberId) {
        const response = await homeService.getHomesByMember({ memberId });
        setHomes(response.data);
      } else {
        throw new Error('HOA id or Member id is required to load home list.');
      }
    } catch (err) {
      console.error('Error fetching homes:', err);
      const errorMessage = apiErrorMessage(err, "Couldn't load homes.");
      showAlert('error', "Couldn't load homes", errorMessage);
    } finally {
      setLoading(false);
    }
  }, [hoaId, memberId, fromStore]);

  useEffect(() => {
    fetchHomes();
  }, [fetchHomes]);

  const visibleHomes = useMemo(() => applyHomeFilters(homes, filters), [homes, filters]);
  const groups = useMemo(() => groupHomes(visibleHomes), [visibleHomes]);

  const handleRowClick = (home: HomeReturn) => {
    navigate(`/hoa/${home.hoa_id}/home/${home.id}`);
  };

  const handleSaveHome = async (newHome: HomeCreate) => {
    try {
      const response = await homeService.createHome({
        homeCreate: { ...newHome, hoa_id: hoaId ?? '' },
      });
      setHomes((prevHomes) => [...prevHomes, response.data]);
      showAlert('success', 'Success', 'Home created successfully!');
      setIsDialogOpen(false);
    } catch (err) {
      console.error('Error creating home:', err);
      const errorMessage = apiErrorMessage(err, "Couldn't create the home.");
      showAlert('error', "Couldn't create home", errorMessage);
    }
  };

  const handleAddHomeToMember = async (data: {
    homeId: string;
    memberId: string;
  }) => {
    try {
      await memberService.addMemberToHome({
        homeId: data.homeId,
        memberId: data.memberId,
      });
      showAlert('success', 'Success', 'Home added to member successfully!');
      await fetchHomes();
    } catch (err) {
      console.error('Error adding home to member:', err);
      const errorMessage = apiErrorMessage(
        err,
        "Couldn't add the home to this member."
      );
      showAlert('error', "Couldn't add home to member", errorMessage);
    }
  };

  const handleRemoveHomes = async () => {
    if (selectedHomes.size === 0 || !member) return;

    try {
      const removalPromises = Array.from(selectedHomes).map((homeId) =>
        memberService.removeMemberFromHome({
          homeId: homeId,
          memberId: member.id,
        })
      );

      await Promise.all(removalPromises);
      await fetchHomes();
      setSelectedHomes(new Set());
      showAlert(
        'success',
        'Success',
        'Homes removed from member successfully!'
      );
    } catch (err) {
      console.error('Error removing homes from member:', err);
      const errorMessage = apiErrorMessage(err, "Couldn't remove the homes.");
      showAlert('error', "Couldn't remove homes", errorMessage);
    }
  };

  // With a synced copy there is nothing to wait for: render it on the
  // first paint, not after the effect clears `loading` (#635).
  if (loading && !fromStore) {
    return <LoadingSpinner></LoadingSpinner>;
  }

  const canAdmin = hasAdminAccess(userType);

  return (
    <>
      <Heading level={4} className="mb-3">
        Homes
      </Heading>

      <ShowAlert
        alertType={alert.type}
        alertTitle={alert.title}
        alertMessage={alert.message}
        isVisible={alert.show}
        onClose={hideAlert}
        timeout={5000}
        isInline={true}
      />

      <div className="overflow-hidden rounded-lg border border-line bg-raised">
        <ListFilterBar
          fields={[
            {
              key: 'statuses',
              label: 'Status',
              options: HOME_STATUSES.map((st) => ({
                value: st.value,
                label: st.label,
                icon: <span className={`h-2 w-2 rounded-full ${st.dot}`} aria-hidden />,
              })),
              values: filters.statuses,
            },
            {
              key: 'types',
              label: 'Type',
              options: PROPERTY_TYPES.map((t) => ({ value: t.value, label: t.label })),
              values: filters.types,
            },
          ]}
          onFieldChange={(key, values) => setFilters((f) => ({ ...f, [key]: values }))}
          search={filters.search}
          onSearch={(search) => setFilters((f) => ({ ...f, search }))}
          searchLabel="Search homes by address"
          shown={visibleHomes.length}
          total={homes.length}
          trailing={
            // In the list's own bar, as on violations and work orders.
            canAdmin && (
              <>
                {hoaId && <CreateButton label="New home" onClick={() => setIsDialogOpen(true)} />}
                {member && (
                  <CreateButton label="Add home" onClick={() => setIsAddToMemberDialogOpen(true)} />
                )}
              </>
            )
          }
        />
        {/* What can be done to the ticked rows, where they are ticked. */}
        {canAdmin && member && (
          <SelectionBar count={selectedHomes.size} onClear={() => setSelectedHomes(new Set())}>
            <button
              type="button"
              onClick={handleRemoveHomes}
              className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-danger hover:bg-fg/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <TrashIcon className="h-3.5 w-3.5" aria-hidden />
              Remove from member
            </button>
          </SelectionBar>
        )}
        <GroupedList
          groups={groups.map((g) => ({
            ...g,
            icon: (
              <span
                className={`h-2 w-2 rounded-full ${HOME_STATUSES.find((st) => st.value === g.key)?.dot ?? 'bg-fg-faint'}`}
                aria-hidden
              />
            ),
          }))}
          getKey={(h) => h.id}
          rowLabel={(h) => `${homeAddressLine(h)}, ${homeLocality(h)}`}
          onOpen={handleRowClick}
          selection={member ? { selected: selectedHomes, onChange: setSelectedHomes } : undefined}
          emptyMessage={homes.length === 0 ? 'No homes yet.' : 'No homes match these filters.'}
          renderRow={(h) => (
            <>
              <span className="min-w-0 flex-1 truncate">
                <span className="font-medium text-fg">{homeAddressLine(h)}</span>
                <span className="ml-2 text-fg-muted">{homeLocality(h)}</span>
              </span>
              <span className="hidden rounded-full border border-line px-2 py-0.5 text-[12px] text-fg-muted sm:inline">
                {propertyTypeLabel(h.property_type)}
              </span>
              {normalizeHomeStatus(h.status) === null && (
                <span className="text-[12px] text-fg-muted">{h.status}</span>
              )}
            </>
          )}
        />
      </div>

      {hoaId && (
        <AddHomeDialog
          isOpen={isDialogOpen}
          hoaId={hoaId ?? 0}
          hoas={hoas}
          onSave={handleSaveHome}
          onClose={() => setIsDialogOpen(false)}
        ></AddHomeDialog>
      )}

      {member && (
        <AddHomeToMemberDialog
          isOpen={isAddToMemberDialogOpen}
          member={member}
          onSave={handleAddHomeToMember}
          onClose={() => setIsAddToMemberDialogOpen(false)}
        />
      )}
    </>
  );
};

export default HomeListComponent;
